import { useState, useMemo } from "react";
import {
  Trash2,
  Search,
  Pencil,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { fromKm, fmt, unitLabel } from "@/lib/units";
import { fmtEfficiency } from "@/lib/efficiency";
import VehicleChipFilter from "@/components/VehicleChipFilter";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function compareValues(a, b, dir) {
  const aNil = a == null || a === "";
  const bNil = b == null || b === "";

  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return dir === "asc" ? a - b : b - a;
  }

  const sa = String(a);
  const sb = String(b);

  return dir === "asc"
    ? sa.localeCompare(sb)
    : sb.localeCompare(sa);
}

function SortableHeader({
  label,
  sortKey,
  sortState,
  onSort,
  align = "left",
  className = "",
}) {
  const active = sortState.key === sortKey;
  const dir = sortState.dir;

  const Icon = !active
    ? ChevronsUpDown
    : dir === "asc"
      ? ChevronUp
      : ChevronDown;

  return (
    <TableHead
      className={`label-tiny text-[var(--text-muted)] ${
        align === "right" ? "text-right" : ""
      } ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 tap hover:text-[var(--text-primary)] transition-colors ${
          active ? "text-[var(--text-primary)]" : ""
        }`}
        data-testid={`trip-sort-${sortKey}`}
      >
        {label}

        <Icon
          className="w-3 h-3"
          strokeWidth={1.75}
        />
      </button>
    </TableHead>
  );
}

export default function TripTable({
  trips = [],
  vehicles = [],
  allVehicles = null,
  unit,
  onDelete,
  onEdit,
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [chipVehicleId, setChipVehicleId] = useState("all");

  const [sortState, setSortState] = useState({
    key: "date",
    dir: "desc",
  });

  // Name resolution uses `allVehicles` (includes archived) so historical rows
  // still resolve to a vehicle name. The chip filter uses `vehicles` (active
  // only) so users never see archived chips.
  const veh = useMemo(() => {
    const m = {};
    (allVehicles || vehicles).forEach((v) => (m[v.id] = v));
    return m;
  }, [allVehicles, vehicles]);

  // Per-active-vehicle row counts — drives the chip counters. Computed from
  // the full incoming trips array (pre-search, pre-chip) so counters stay
  // stable as the user types or switches chips.
  const counts = useMemo(() => {
    const c = {};
    vehicles.forEach((v) => (c[v.id] = 0));
    for (const t of trips) {
      if (c[t.vehicle_id] != null) c[t.vehicle_id] += 1;
    }
    return c;
  }, [vehicles, trips]);

  const filtered = useMemo(() => {
    let base = trips;
    // In-context chip filter (overrides nothing globally; applied first so
    // the row counter + sort + pagination all reflect the active chip).
    if (chipVehicleId !== "all") {
      base = base.filter((t) => t.vehicle_id === chipVehicleId);
    }
    if (!q.trim()) return base;

    const needle = q.toLowerCase();

    return base.filter(
      (t) =>
        (t.notes || "").toLowerCase().includes(needle) ||
        (veh[t.vehicle_id]?.name || "")
          .toLowerCase()
          .includes(needle) ||
        (t.usage_type || "")
          .toLowerCase()
          .includes(needle) ||
        (t.date || "").includes(needle)
    );
  }, [trips, q, veh, chipVehicleId]);

  const sorted = useMemo(() => {
    const acc = (t) => {
      switch (sortState.key) {
        case "date":
          return t.date;

        case "vehicle":
          return veh[t.vehicle_id]?.name || "";

        case "distance":
          return t.distance_km;

        case "efficiency":
          return t.efficiency_km_per_kwh;

        case "usage":
          return t.usage_type || "";

        default:
          return null;
      }
    };

    return [...filtered].sort((a, b) => {
      const primary = compareValues(acc(a), acc(b), sortState.dir);
      if (primary !== 0) return primary;
      // Stable tie-breaker: most-recently-logged surfaces first within a tie
      // (e.g. two trips on the same date). created_at is an ISO string from
      // the backend, sortable lexically.
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      return cb.localeCompare(ca);
    });
  }, [filtered, sortState, veh]);

  const totalPages = Math.max(
    1,
    Math.ceil(sorted.length / rowsPerPage)
  );

  const safePage = Math.min(page, totalPages - 1);

  const pageRows = useMemo(() => {
    const start = safePage * rowsPerPage;

    return sorted.slice(
      start,
      start + rowsPerPage
    );
  }, [sorted, safePage, rowsPerPage]);

  const handleSort = (key) => {
    setSortState((s) =>
      s.key === key
        ? {
            key,
            dir:
              s.dir === "asc"
                ? "desc"
                : "asc",
          }
        : {
            key,
            dir: "desc",
          }
    );

    setPage(0);
  };

  return (
    <div
      className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] rounded-lg flex flex-col h-full"
      data-testid="trip-history-table"
    >
      <div className="p-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium text-[var(--text-primary)]">
            Trip History
          </h3>

          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            {filtered.length}{" "}
            {filtered.length === 1
              ? "trip"
              : "trips"}
          </p>
        </div>

        <div className="relative">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
            strokeWidth={1.5}
          />

          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search..."
            className="pl-9 w-[180px] sm:w-[220px] bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] h-9 focus-visible:ring-1 focus-visible:ring-[#E82127]/40"
            data-testid="trip-search-input"
          />
        </div>
      </div>

      {vehicles.length > 1 && (
        <div className="px-5 pb-3">
          <VehicleChipFilter
            vehicles={vehicles}
            value={chipVehicleId}
            onChange={(next) => {
              setChipVehicleId(next);
              setPage(0);
            }}
            counts={counts}
            totalCount={trips.length}
            testIdPrefix="trip"
          />
        </div>
      )}

      <div className="overflow-auto flex-1 max-h-[460px]">
        <Table>
          <TableHeader className="sticky top-0 bg-[var(--bg-elevated)] z-10">
            <TableRow className="border-[var(--border-hover)] hover:bg-transparent">
              <SortableHeader
                label="Date"
                sortKey="date"
                sortState={sortState}
                onSort={handleSort}
              />

              <SortableHeader
                label="Vehicle"
                sortKey="vehicle"
                sortState={sortState}
                onSort={handleSort}
              />

              <SortableHeader
                label={`Distance (${unitLabel(unit)})`}
                sortKey="distance"
                sortState={sortState}
                onSort={handleSort}
                align="right"
              />

              <SortableHeader
                label="Efficiency"
                sortKey="efficiency"
                sortState={sortState}
                onSort={handleSort}
                align="right"
                className="hidden md:table-cell"
              />

              <SortableHeader
                label="Usage"
                sortKey="usage"
                sortState={sortState}
                onSort={handleSort}
              />

              <TableHead className="label-tiny text-[var(--text-muted)]">
                Notes
              </TableHead>

              <TableHead className="text-right w-[88px]"></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow className="hover:bg-transparent border-[var(--border-hover)]">
                <TableCell
                  colSpan={7}
                  className="text-center py-12 text-[var(--text-muted)]"
                >
                  No trips logged yet. Add one or import a CSV.
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((t) => {
                const usage =
                  t.usage_type || "personal";

                const isBusiness =
                  usage === "business";

                const eff = fmtEfficiency(
                  t,
                  unit
                );

                return (
                  <TableRow
                    key={t.id}
                    className="border-[var(--border-hover)] hover:bg-[var(--bg-hover)] group"
                    data-testid={`trip-row-${t.id}`}
                  >
                    <TableCell className="font-mono-data text-[var(--text-primary)] text-sm">
                      {t.date}
                    </TableCell>

                    <TableCell className="text-[var(--text-primary)] text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{
                            background:
                              veh[t.vehicle_id]
                                ?.color ||
                              "#8A8A8E",
                          }}
                        />

                        {veh[t.vehicle_id]
                          ?.name || "Unknown"}
                      </div>
                    </TableCell>

                    <TableCell className="font-mono-data text-[var(--text-primary)] text-right">
                      {fmt(
                        fromKm(
                          t.distance_km,
                          unit
                        ),
                        1
                      )}
                    </TableCell>

                    <TableCell
                      className="font-mono-data text-right hidden md:table-cell"
                      data-testid={`trip-efficiency-${t.id}`}
                    >
                      {eff?.primary ? (
                        <div className="leading-tight">
                          <div className="text-[var(--text-primary)] text-xs">
                            {eff.primary}
                          </div>

                          {eff.secondary && (
                            <div className="text-[10px] text-[var(--text-faint)]">
                              {eff.secondary}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[var(--text-faint)]">
                          —
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-wider uppercase border ${
                          isBusiness
                            ? "border-white/30 text-[var(--text-primary)] bg-[var(--bg-inverse)]/5"
                            : "border-[var(--border-subtle)] text-[var(--text-muted)]"
                        }`}
                        style={{
                          borderRadius: 2,
                        }}
                        data-testid={`trip-usage-${t.id}`}
                      >
                        {isBusiness
                          ? "Business"
                          : "Personal"}
                      </span>
                    </TableCell>

                    <TableCell className="text-[#a8a8ae] text-sm max-w-[300px] truncate">
                      {t.notes || (
                        <span className="text-[var(--text-faint)]">
                          —
                        </span>
                      )}
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-transparent"
                            onClick={() =>
                              onEdit(t)
                            }
                            data-testid={`trip-edit-${t.id}`}
                            aria-label="Edit trip"
                          >
                            <Pencil
                              className="w-3.5 h-3.5"
                              strokeWidth={
                                1.5
                              }
                            />
                          </Button>
                        )}

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-[var(--text-muted)] hover:text-[#FF3B30] hover:bg-transparent"
                          onClick={() =>
                            onDelete(t.id)
                          }
                          data-testid={`trip-delete-${t.id}`}
                          aria-label="Delete trip"
                        >
                          <Trash2
                            className="w-3.5 h-3.5"
                            strokeWidth={
                              1.5
                            }
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="px-5 py-3 border-t border-[var(--border-hover)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
          <span>Rows per page</span>

          <Select
            value={String(rowsPerPage)}
            onValueChange={(v) => {
              setRowsPerPage(
                parseInt(v, 10)
              );

              setPage(0);
            }}
          >
            <SelectTrigger
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] h-7 w-[72px]"
              data-testid="trip-rows-per-page"
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
              {ROWS_PER_PAGE_OPTIONS.map(
                (n) => (
                  <SelectItem
                    key={n}
                    value={String(n)}
                  >
                    {n}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
          <span data-testid="trip-page-indicator">
            Page{" "}
            <span className="text-[var(--text-primary)]">
              {safePage + 1}
            </span>{" "}
            of {totalPages} ·{" "}
            <span className="text-[var(--text-primary)]">
              {sorted.length}
            </span>{" "}
            total
          </span>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)] disabled:opacity-40"
              disabled={safePage <= 0}
              onClick={() =>
                setPage((p) =>
                  Math.max(0, p - 1)
                )
              }
              data-testid="trip-prev-page"
              aria-label="Previous page"
            >
              <ChevronLeft
                className="w-3.5 h-3.5"
                strokeWidth={1.75}
              />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)] disabled:opacity-40"
              disabled={
                safePage >= totalPages - 1
              }
              onClick={() =>
                setPage((p) =>
                  Math.min(
                    totalPages - 1,
                    p + 1
                  )
                )
              }
              data-testid="trip-next-page"
              aria-label="Next page"
            >
              <ChevronRight
                className="w-3.5 h-3.5"
                strokeWidth={1.75}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}